import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { AuditLogsFilterParams, AuditLogType, AuditLogsListResponse } from '@/types/audit-log'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchAuditLogs, fetchAuditLog, deleteAuditLog, bulkDeleteAuditLogs } from '@/lib/api'
import { SortEnum } from '@/enums'

type AuditLogStateType = {
  isLoading: boolean
  response: AuditLogType | null
  error: {
    status?: number
    message: string
  } | null
}

type AuditLogsListStateType = {
  isLoading: boolean
  response: AuditLogsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type AuditLogsStoreState = {
  // Filters state
  filters: AuditLogsFilterParams
  setFilters: (filters: Partial<AuditLogsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // List state
  auditLogsListState: AuditLogsListStateType
  fetchAuditLogs: (params?: AuditLogsFilterParams) => Promise<AuditLogsListResponse>
  resetAuditLogsList: () => void

  // Single state
  auditLogState: AuditLogStateType
  getAuditLog: (auditLogId: string) => Promise<AuditLogType>
  deleteAuditLog: (auditLogId: string) => Promise<void>
  resetAuditLog: () => void

  // Selection operations
  selectedAuditLogIds: string[]
  selectAuditLog: (id: string) => void
  deselectAuditLog: (id: string) => void
  toggleAuditLogSelection: (id: string) => void
  selectAllAuditLogs: (auditLogs: AuditLogType[]) => void
  deselectAllAuditLogs: () => void
  isAuditLogSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteAuditLogs: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: AuditLogsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialAuditLogState: AuditLogStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialAuditLogsListState: AuditLogsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useAuditLogsStore = create<AuditLogsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    auditLogState: initialAuditLogState,
    auditLogsListState: initialAuditLogsListState,
    selectedAuditLogIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof AuditLogsFilterParams]
          } else {
            updatedFilters[key as keyof AuditLogsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: AuditLogsFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as AuditLogsFilterParams['sort']
            break
          case 'sortBy':
            filters[key] = value as AuditLogsFilterParams['sortBy']
            break
          case 'userIds':
          case 'modules':
          case 'actions':
          case 'results':
            filters[key] = value.split(',') as any
            break
          default:
            filters[key as keyof AuditLogsFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    // List operations
    fetchAuditLogs: async (params?: AuditLogsFilterParams) => {
      const currentState = get().auditLogsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        auditLogsListState: {
          ...initialAuditLogsListState,
          isLoading: true
        }
      }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchAuditLogs(filtersToUse)

        set(() => ({
          auditLogsListState: {
            ...initialAuditLogsListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          auditLogsListState: {
            ...initialAuditLogsListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetAuditLogsList: () => {
      set(() => ({
        auditLogsListState: initialAuditLogsListState
      }))
    },

    // Single operations
    getAuditLog: async (auditLogId: string) => {
      const currentState = get().auditLogState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        auditLogState: {
          ...initialAuditLogState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchAuditLog(auditLogId)

        set(() => ({
          auditLogState: {
            ...initialAuditLogState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          auditLogState: {
            ...initialAuditLogState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteAuditLog: async (auditLogId: string) => {
      set(() => ({
        auditLogState: {
          ...get().auditLogState,
          isLoading: true
        }
      }))

      try {
        await deleteAuditLog(auditLogId)

        set(() => ({
          auditLogState: initialAuditLogState
        }))
      } catch (error: any) {
        set(() => ({
          auditLogState: {
            ...initialAuditLogState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetAuditLog: () => {
      set(() => ({
        auditLogState: initialAuditLogState
      }))
    },

    // Selection operations
    selectAuditLog: id => {
      set(state => {
        if (state.selectedAuditLogIds.includes(id)) {
          return state
        }
        return { selectedAuditLogIds: [...state.selectedAuditLogIds, id] }
      })
    },

    deselectAuditLog: id => {
      set(state => ({
        selectedAuditLogIds: state.selectedAuditLogIds.filter(auditLogId => auditLogId !== id)
      }))
    },

    toggleAuditLogSelection: id => {
      set(state => {
        if (state.selectedAuditLogIds.includes(id)) {
          return { selectedAuditLogIds: state.selectedAuditLogIds.filter(auditLogId => auditLogId !== id) }
        }
        return { selectedAuditLogIds: [...state.selectedAuditLogIds, id] }
      })
    },

    selectAllAuditLogs: auditLogs => {
      set(() => ({
        selectedAuditLogIds: auditLogs.map(auditLog => auditLog.id)
      }))
    },

    deselectAllAuditLogs: () => {
      set(() => ({ selectedAuditLogIds: [] }))
    },

    isAuditLogSelected: id => {
      return get().selectedAuditLogIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteAuditLogs: async ids => {
      set(state => ({
        auditLogState: {
          ...state.auditLogState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteAuditLogs({ ids })

        set(state => ({
          auditLogState: {
            ...state.auditLogState,
            isLoading: false
          },
          selectedAuditLogIds: state.selectedAuditLogIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          auditLogState: {
            ...state.auditLogState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useAuditLogsStore
